import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { RefreshCw, Loader2, AlertCircle, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

/**
 * Canon CCMS — aylık sosyal medya içerik raporu (plan ↔ gerçekleşen).
 *
 * Haziran ve Temmuz'da bu rapor müşterinin kendi Excel'ine elle dolduruluyordu
 * (CEA sheet, J/K/L/M sütunları). Ağustos'tan itibaren panelde canlı sayfa:
 * veri her açılışta Meta Graph API'den gelir, DB'den DEĞİL.
 *
 * Backend: GET /api/canon-report/ccms?month=YYYY-MM
 *
 * İki metrik kuralı sayfanın her yerinde geçerli:
 *  1) ER, FB/IG kırılımından hesaplanır — toplamdan değil. Otomatik yerleşim
 *     Audience Network / Threads'e de dağıtıyor, o gösterimler KPI dışı.
 *  2) Video reklamlarda post_engagement'ın büyük kısmı video_view'dur; ana
 *     etkileşim metriği "video hariç"tir, video izlenme ayrı satırda durur.
 */

const API_URL = 'https://api.glowytics.com';
const CANON_BRAND_ID = '3987361f-4006-4819-bd83-f78b5a2155ec';
const CANON_RED = '#CC0000';

const fmt = (n) => (n == null ? '—' : Math.round(Number(n)).toLocaleString('tr-TR'));
const money = (n) => (n == null ? '—' : `${Math.round(Number(n)).toLocaleString('tr-TR')} ₺`);
const money2 = (n) => (n == null ? '—' : `${Number(n).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`);
const pct = (n) => (n == null ? '—' : `%${Number(n).toFixed(2)}`);

const PLATFORM_LABEL = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  audience_network: 'Audience Network',
  threads: 'Threads',
  whatsapp: 'WhatsApp',
  messenger: 'Messenger',
  unknown: 'Bilinmeyen',
};

const OBJ_LABEL = {
  OUTCOME_TRAFFIC: 'Traffic',
  OUTCOME_ENGAGEMENT: 'Engagement',
  OUTCOME_AWARENESS: 'Awareness',
  LINK_CLICKS: 'Traffic',
  POST_ENGAGEMENT: 'Engagement',
};

export default function CanonCcmsMonthlyReport({ month = '2026-08' }) {
  const slideRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [openRow, setOpenRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data: d } = await axios.get(`${API_URL}/api/canon-report/ccms`, {
        params: { month },
        headers: { 'X-Brand-ID': CANON_BRAND_ID },
        withCredentials: true,
      });
      setData(d);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const exportPdf = useCallback(async () => {
    if (exporting || !slideRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(slideRef.current, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
      const img = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const m = 8;
      const w = pw - m * 2;
      const h = (canvas.height * w) / canvas.width;
      let y = m, rem = h;
      pdf.addImage(img, 'JPEG', m, y, w, h);
      rem -= (ph - m * 2);
      while (rem > 0) {
        pdf.addPage();
        y = m - (h - rem);
        pdf.addImage(img, 'JPEG', m, y, w, h);
        rem -= (ph - m * 2);
      }
      pdf.save(`Canon_CCMS_Raporu_${month}.pdf`);
    } finally {
      setExporting(false);
    }
  }, [exporting, month]);

  const Stat = ({ label, value, sub, tone = 'gray' }) => {
    const tones = {
      gray: 'border-gray-200 bg-gray-50 text-gray-800',
      red: 'border-red-100 bg-red-50 text-red-700',
      indigo: 'border-indigo-100 bg-indigo-50 text-indigo-700',
      green: 'border-emerald-100 bg-emerald-50 text-emerald-700',
      amber: 'border-amber-200 bg-amber-50 text-amber-800',
    };
    return (
      <div className={`rounded-xl border p-4 ${tones[tone]}`}>
        <span className="text-[10px] font-medium uppercase opacity-70">{label}</span>
        <p className="text-xl font-bold mt-1 tabular-nums">{value}</p>
        {sub && <p className="text-[10px] opacity-60 mt-0.5">{sub}</p>}
      </div>
    );
  };

  if (loading && !data) {
    return (
      <div className="p-10 flex items-center justify-center gap-3 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin" /> Canlı Meta verisi çekiliyor…
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-red-800">Rapor yüklenemedi</p>
            <p className="text-sm text-red-700 mt-1 break-all">{error}</p>
            <button onClick={load} className="mt-3 text-sm font-semibold text-red-700 underline">Tekrar dene</button>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { plan, kpi, pillars, campaigns, totals, uniqueReach, platformTotals, offPlacements } = data;
  const fb = platformTotals?.facebook;
  const ig = platformTotals?.instagram;
  const planNet = plan.net;
  const realised = totals?.spend || 0;
  const overspend = realised - planNet;
  const byId = new Map(campaigns.map((c) => [c.campaignId, c]));

  const KpiChip = ({ ok, children }) => (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border ${
      ok ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'
    }`}>
      {ok ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
      {children}
    </span>
  );

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <div className="flex justify-end gap-2 mb-3">
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 text-gray-700 bg-white border border-gray-300 text-sm font-semibold px-4 py-2 rounded-lg shadow-sm disabled:opacity-60"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Yenile
        </button>
        <button
          onClick={exportPdf}
          disabled={exporting}
          className="text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm disabled:opacity-60"
          style={{ background: CANON_RED }}
        >
          {exporting ? 'PDF Oluşturuluyor…' : 'PDF Olarak İndir'}
        </button>
      </div>

      <div ref={slideRef} className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/Canon_logo_vector.png" alt="Canon" className="h-12 w-auto object-contain" />
            <div className="h-8 w-px bg-gray-200" />
            <div>
              <p className="text-xs font-bold text-gray-800">
                CCMS — Sosyal Medya İçerik Raporu · {plan.label}
              </p>
              <p className="text-[9px] text-gray-400">
                Meta Ads (FB + IG) · plan ↔ gerçekleşen · veri {data.since} – {data.until} penceresinden canlı çekildi
              </p>
            </div>
          </div>
          <img src="/Jellybon-digital-agency-istanbul.webp" alt="Jellybon" className="h-12 w-auto object-contain" />
        </div>

        <div className="p-6 space-y-6">
          {/* Özet */}
          <div className="rounded-xl border border-red-100 bg-red-50 p-4">
            <p className="text-sm font-bold text-gray-800">Ay Özeti</p>
            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
              {plan.label} CCMS akışında <strong>{campaigns.length} kampanya</strong> yayına çıktı ve
              toplam <strong>{money2(realised)}</strong> harcandı. Plandaki net medya bütçesi <strong>{money(planNet)}</strong> idi;
              gerçekleşme <strong>%{Math.round((realised / planNet) * 100)}</strong>.
              {plan.theme && <> Ayın içerik teması <strong>{plan.theme}</strong>.</>}
              {' '}Gösterim <strong>{fmt(totals.impressions)}</strong>, tekil erişim <strong>{fmt(uniqueReach ?? totals.reach)}</strong>,
              video hariç etkileşim <strong>{fmt(totals.engagementExVideo)}</strong>.
            </p>
          </div>

          {/* KPI kartları */}
          <div>
            <p className="text-sm font-bold text-gray-800 mb-2">Genel Sonuç</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Harcama" value={money2(realised)} sub={`plan ${money(planNet)}`} tone={overspend > planNet * 0.02 ? 'amber' : 'green'} />
              <Stat label="Gösterim" value={fmt(totals.impressions)} sub={`CPM ${money2(totals.cpm)}`} />
              <Stat label="Tekil Erişim" value={fmt(uniqueReach ?? totals.reach)} sub={uniqueReach ? `satır toplamı ${fmt(totals.reach)}` : 'satır toplamı'} tone="green" />
              <Stat label="Etkileşim (video hariç)" value={fmt(totals.engagementExVideo)} sub={`video izlenme ${fmt(totals.videoViews)} ayrı`} tone="indigo" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
              <Stat label="Link Tıklaması" value={fmt(totals.linkClicks)} sub={totals.linkClicks ? `${money2(realised / totals.linkClicks)} / tık` : '—'} tone="red" />
              <Stat label="Frekans" value={(uniqueReach ? totals.impressions / uniqueReach : 0).toFixed(2)} sub="gösterim ÷ tekil erişim" />
              <Stat
                label="Facebook ER"
                value={pct(fb?.er)}
                sub={`hedef %${kpi.fbEr.toFixed(2)} · ${fmt(fb?.impressions)} gösterim`}
                tone={(fb?.er || 0) >= kpi.fbEr ? 'green' : 'red'}
              />
              <Stat
                label="Instagram ER"
                value={pct(ig?.er)}
                sub={`hedef %${kpi.igEr.toFixed(2)} · ${fmt(ig?.impressions)} gösterim`}
                tone={(ig?.er || 0) >= kpi.igEr ? 'green' : 'red'}
              />
            </div>
            <p className="text-[10px] text-gray-400 mt-2 leading-relaxed">
              ER = etkileşim ÷ gösterim, müşterinin <strong>2026 KPIs</strong> sheet'indeki tanım.
              Değerler <strong>Facebook ve Instagram kırılımından</strong> hesaplanır, toplamdan değil: otomatik yerleşim
              gösterimin bir kısmını Audience Network ve Threads'e dağıtıyor, oralarda etkileşimin neredeyse tamamı
              video izlenme olduğu için toplam ER şişer. Video reklamlarda etkileşim <strong>video izlenme hariç</strong> alınır.
            </p>
          </div>

          {/* Pillar tablosu */}
          <div>
            <p className="text-sm font-bold text-gray-800 mb-2">Pillar Bazında Plan ↔ Gerçekleşen</p>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 uppercase text-[10px]">
                    <th className="text-left px-3 py-2 font-semibold">Frame</th>
                    <th className="text-left px-3 py-2 font-semibold">Pillar</th>
                    <th className="text-center px-3 py-2 font-semibold">Hedef</th>
                    <th className="text-right px-3 py-2 font-semibold">Plan</th>
                    <th className="text-right px-3 py-2 font-semibold">Gerçekleşen</th>
                    <th className="text-right px-3 py-2 font-semibold">%</th>
                    <th className="text-right px-3 py-2 font-semibold">Gösterim</th>
                    <th className="text-right px-3 py-2 font-semibold">CPM</th>
                    <th className="text-right px-3 py-2 font-semibold">Etkileşim*</th>
                    <th className="text-right px-3 py-2 font-semibold">ER*</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pillars.map((p) => {
                    const ratio = (p.spendRatio || 0) * 100;
                    const over = ratio > 105;
                    const under = ratio < 90;
                    return (
                      <tr key={p.key} className={over ? 'bg-amber-50/70' : under ? 'bg-red-50/50' : 'hover:bg-gray-50'}>
                        <td className="px-3 py-2 text-gray-500">{p.frame}</td>
                        <td className="px-3 py-2 font-semibold text-gray-800">
                          {p.label}
                          <span className="block text-[9px] font-normal text-gray-400 font-mono">{p.sprout} · {p.campaignCount} kampanya</span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded-md text-[10px] font-semibold border ${
                            p.objective === 'Traffic' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-violet-50 text-violet-700 border-violet-200'
                          }`}>{p.objective}</span>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{money(p.budget)}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold">{money2(p.actual.spend)}</td>
                        <td className={`px-3 py-2 text-right tabular-nums font-bold ${over ? 'text-amber-700' : under ? 'text-red-700' : 'text-emerald-700'}`}>
                          %{Math.round(ratio)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmt(p.actual.impressions)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{money2(p.actual.cpm)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmt(p.actual.engagementExVideo)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{pct(p.actual.engagementExVideoRate)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-red-50 font-bold text-gray-800">
                    <td className="px-3 py-2" colSpan={3}>TOPLAM</td>
                    <td className="px-3 py-2 text-right tabular-nums">{money(planNet)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{money2(realised)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">%{Math.round((realised / planNet) * 100)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(totals.impressions)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{money2(totals.cpm)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(totals.engagementExVideo)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{pct(totals.engagementExVideoRate)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <p className="text-[10px] text-gray-400 mt-2">
              *Etkileşim ve ER video izlenme hariçtir. Plan sütunu post başı {money(plan.perPost)} × pillar'ın post sayısıdır;
              kampanyalar {plan.liveDays} gün yayında kalır.
            </p>
          </div>

          {/* Yerleşim dağılımı */}
          <div>
            <p className="text-sm font-bold text-gray-800 mb-2">Yerleşim Dağılımı</p>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 uppercase text-[10px]">
                    <th className="text-left px-3 py-2 font-semibold">Yerleşim</th>
                    <th className="text-center px-3 py-2 font-semibold">KPI</th>
                    <th className="text-right px-3 py-2 font-semibold">Harcama</th>
                    <th className="text-right px-3 py-2 font-semibold">Pay</th>
                    <th className="text-right px-3 py-2 font-semibold">Gösterim</th>
                    <th className="text-right px-3 py-2 font-semibold">CPM</th>
                    <th className="text-right px-3 py-2 font-semibold">Link Tık</th>
                    <th className="text-right px-3 py-2 font-semibold">ER</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {Object.entries(platformTotals)
                    .filter(([, v]) => v.impressions > 0 || v.spend > 0)
                    .sort((a, b) => b[1].spend - a[1].spend)
                    .map(([k, v]) => {
                      const inKpi = k === 'facebook' || k === 'instagram';
                      return (
                        <tr key={k} className={inKpi ? 'hover:bg-gray-50' : 'bg-amber-50/60'}>
                          <td className="px-3 py-2 font-semibold text-gray-800">{PLATFORM_LABEL[k] || k}</td>
                          <td className="px-3 py-2 text-center text-[10px]">
                            {inKpi
                              ? <span className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">içinde</span>
                              : <span className="px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 border border-amber-300 font-semibold">dışında</span>}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">{money2(v.spend)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">%{((v.spend / realised) * 100).toFixed(1)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{fmt(v.impressions)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{money2(v.cpm)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{fmt(v.linkClicks)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{pct(v.er)}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
            {offPlacements.spend > 0 && (
              <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-[11px] text-amber-900 leading-relaxed">
                  Plan <strong>FB + IG</strong> diyor, ancak otomatik yerleşim bütçenin
                  <strong> {money2(offPlacements.spend)}</strong>'sini (%{offPlacements.spendShare.toFixed(1)}) KPI dışındaki
                  yerleşimlere dağıttı: {fmt(offPlacements.impressions)} gösterim (%{offPlacements.impressionShare.toFixed(1)}).
                  {platformTotals.audience_network && (
                    <> Audience Network tek başına <strong>{money2(platformTotals.audience_network.spend)}</strong> harcayıp
                    ER %{platformTotals.audience_network.er.toFixed(2)} ürettiği için bu yerleşimin adset seviyesinde kapatılmasını öneriyoruz.</>
                  )}
                </p>
              </div>
            )}
          </div>

          {/* Kampanya detayı */}
          <div>
            <p className="text-sm font-bold text-gray-800 mb-2">Kampanya Detayı ({campaigns.length})</p>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 uppercase text-[10px]">
                    <th className="text-left px-3 py-2 font-semibold">Yayın</th>
                    <th className="text-left px-3 py-2 font-semibold">İçerik</th>
                    <th className="text-right px-3 py-2 font-semibold">Harcama</th>
                    <th className="text-right px-3 py-2 font-semibold">Gösterim</th>
                    <th className="text-right px-3 py-2 font-semibold">Erişim</th>
                    <th className="text-right px-3 py-2 font-semibold">CPM</th>
                    <th className="text-right px-3 py-2 font-semibold">Link Tık</th>
                    <th className="text-right px-3 py-2 font-semibold">CTR</th>
                    <th className="text-right px-3 py-2 font-semibold">Etkileşim*</th>
                    <th className="text-center px-2 py-2 font-semibold w-24">FB / IG ER</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {campaigns.map((c) => {
                    const a = c.actual;
                    const cfb = c.platforms?.facebook;
                    const cig = c.platforms?.instagram;
                    const fbEr = cfb ? cfb.engagementExVideoRate : null;
                    const igEr = cig ? cig.engagementExVideoRate : null;
                    const open = openRow === c.campaignId;
                    return (
                      <React.Fragment key={c.campaignId}>
                        <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => setOpenRow(open ? null : c.campaignId)}>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                            {c.start}
                            <span className="block text-[9px] text-gray-400">{c.stop ? `→ ${c.stop}` : 'devam ediyor'}</span>
                          </td>
                          <td className="px-3 py-2 max-w-xs">
                            <span className="font-semibold text-gray-800">
                              {pillars.find((p) => p.key === c.pillar)?.label || c.pillar}
                            </span>
                            {c.isVideo && <span className="ml-1 text-[9px] font-bold uppercase text-violet-600">· video</span>}
                            <span className="block text-[10px] text-gray-500 truncate">{c.caption.split('\n')[0]}</span>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums font-semibold">{money2(a.spend)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{fmt(a.impressions)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{fmt(a.reach)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{money2(a.cpm)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{fmt(a.linkClicks)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{pct(a.impressions ? (a.linkClicks / a.impressions) * 100 : 0)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {fmt(a.engagementExVideo)}
                            {c.isVideo && <span className="block text-[9px] text-gray-400">video {fmt(a.videoViews)}</span>}
                          </td>
                          <td className="px-2 py-2">
                            <div className="flex flex-col items-stretch gap-1">
                              {fbEr != null && <KpiChip ok={fbEr >= kpi.fbEr}>FB {pct(fbEr)}</KpiChip>}
                              {igEr != null && <KpiChip ok={igEr >= kpi.igEr}>IG {pct(igEr)}</KpiChip>}
                            </div>
                          </td>
                        </tr>
                        {open && (
                          <tr className="bg-gray-50">
                            <td colSpan={10} className="px-4 py-3">
                              <div className="grid md:grid-cols-3 gap-4">
                                <div className="md:col-span-2">
                                  <p className="text-[10px] uppercase font-semibold text-gray-400 mb-1">Caption</p>
                                  <p className="text-[11px] text-gray-700 whitespace-pre-line leading-relaxed">{c.caption || '—'}</p>
                                  <p className="text-[10px] uppercase font-semibold text-gray-400 mt-3 mb-1">Kampanya adı (Meta'daki hâliyle)</p>
                                  <p className="text-[10px] font-mono text-gray-600 break-all">{c.name}</p>
                                  <p className="text-[10px] text-gray-400 mt-1">
                                    Hedef: {OBJ_LABEL[c.objective] || c.objective || '—'} · Durum: {c.status} · {c.ads.length} reklam
                                    {c.permalink && <> · <a href={c.permalink} target="_blank" rel="noreferrer" className="text-blue-600 underline">gönderiyi aç</a></>}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-[10px] uppercase font-semibold text-gray-400 mb-1">Yerleşim kırılımı</p>
                                  <table className="w-full text-[10px]">
                                    <tbody className="divide-y divide-gray-200">
                                      {Object.entries(c.platforms)
                                        .filter(([, v]) => v.impressions > 0)
                                        .sort((x, y) => y[1].spend - x[1].spend)
                                        .map(([k, v]) => (
                                          <tr key={k}>
                                            <td className="py-1 text-gray-600">{PLATFORM_LABEL[k] || k}</td>
                                            <td className="py-1 text-right tabular-nums">{money2(v.spend)}</td>
                                            <td className="py-1 text-right tabular-nums">{fmt(v.impressions)}</td>
                                            <td className="py-1 text-right tabular-nums">{pct(v.engagementExVideoRate)}</td>
                                          </tr>
                                        ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-gray-400 mt-2">
              Satıra tıklayınca caption, kampanya adı ve yerleşim kırılımı açılır.
              Erişim sütunu <strong>kampanya bazındadır</strong>; aynı kişi birden fazla kampanyada görülebildiği için
              satırlar toplanmaz — ay geneli tekil erişim yukarıdaki karttadır.
            </p>
          </div>

          {/* Hüküm */}
          <div>
            <p className="text-sm font-bold text-gray-800 mb-2">Değerlendirme</p>
            <div className="space-y-2">
              {(() => {
                const notes = [];
                const heavy = pillars.filter((p) => (p.spendRatio || 0) > 1.05);
                const light = pillars.filter((p) => (p.spendRatio || 0) < 0.9);
                if (heavy.length) {
                  notes.push({
                    tone: 'amber',
                    text: `${heavy.map((p) => p.label).join(', ')} plan bütçesini aştı: toplam ${money2(heavy.reduce((s, p) => s + p.actual.spend, 0))} harcandı, plan ${money(heavy.reduce((s, p) => s + p.budget, 0))} idi. Ayın ${money2(overspend)} aşımının tamamı buradan geliyor.`,
                  });
                }
                if (light.length) {
                  notes.push({
                    tone: 'red',
                    text: `${light.map((p) => p.label).join(', ')} bütçesini bitiremedi; kullanılmayan tutar ${money2(light.reduce((s, p) => s + (p.budget - p.actual.spend), 0))}.`,
                  });
                }
                const fbOk = (fb?.er || 0) >= kpi.fbEr;
                const igOk = (ig?.er || 0) >= kpi.igEr;
                notes.push({
                  tone: fbOk && igOk ? 'green' : 'red',
                  text: fbOk && igOk
                    ? `Her iki KPI da tutturuldu: Facebook ER %${(fb?.er || 0).toFixed(2)} (hedef %${kpi.fbEr.toFixed(2)}), Instagram ER %${(ig?.er || 0).toFixed(2)} (hedef %${kpi.igEr.toFixed(2)}).`
                    : `KPI tutturulamadı: Facebook ER %${(fb?.er || 0).toFixed(2)} / hedef %${kpi.fbEr.toFixed(2)}, Instagram ER %${(ig?.er || 0).toFixed(2)} / hedef %${kpi.igEr.toFixed(2)}.`,
                });
                if (ig && fb && ig.er > fb.er * 1.5) {
                  notes.push({
                    tone: 'gray',
                    text: `Instagram etkileşimde Facebook'un ${(ig.er / fb.er).toFixed(1)} katı verim üretiyor (ER %${ig.er.toFixed(2)} ↔ %${fb.er.toFixed(2)}), buna karşılık gösterim başına maliyeti ${money2(ig.cpm)} ↔ ${money2(fb.cpm)}. Etkileşim hedefli pillar'larda ağırlık Instagram'a kaydırılabilir.`,
                  });
                }
                if (offPlacements.spend > realised * 0.05) {
                  notes.push({
                    tone: 'amber',
                    text: `Bütçenin %${offPlacements.spendShare.toFixed(1)}'i KPI dışı yerleşimlere gitti (${money2(offPlacements.spend)}). Bu gösterimler ER hesabına girmiyor ama bütçeden düşüyor; adset seviyesinde yerleşim kısıtı öneriyoruz.`,
                  });
                }
                // Planda Traffic yazan pillar Engagement hedefiyle satın alındıysa link tıklaması
                // ölçülemez hâle gelir — bu ay Tuesday Tip ve Technology Highlight'ta olan bu.
                const mismatched = pillars
                  .filter((p) => p.objective === 'Traffic')
                  .map((p) => ({
                    p,
                    camps: p.campaigns.map((id) => byId.get(id)).filter((c) => c && OBJ_LABEL[c.objective] && OBJ_LABEL[c.objective] !== 'Traffic'),
                  }))
                  .filter((x) => x.camps.length);
                if (mismatched.length) {
                  const spend = mismatched.reduce((s, x) => s + x.camps.reduce((t, c) => t + c.actual.spend, 0), 0);
                  const clicks = mismatched.reduce((s, x) => s + x.camps.reduce((t, c) => t + c.actual.linkClicks, 0), 0);
                  notes.push({
                    tone: 'red',
                    text: `Planda hedefi Traffic olan ${mismatched.map((x) => x.p.label).join(' ve ')} kampanyaları Meta'da `
                      + `${[...new Set(mismatched.flatMap((x) => x.camps.map((c) => OBJ_LABEL[c.objective])))].join(' / ')} hedefiyle açılmış. `
                      + `${money2(spend)} harcamaya karşılık yalnızca ${fmt(clicks)} link tıklaması geldi; `
                      + `Meta bu kampanyalarda tıklamaya değil etkileşime optimize ettiği için trafik hedefi ölçülemedi. `
                      + `Gelecek ayda bu iki pillar Traffic hedefiyle kurulmalı.`,
                  });
                }
                return notes.map((n, i) => (
                  <div key={i} className={`rounded-lg border p-3 flex items-start gap-2 ${
                    n.tone === 'green' ? 'border-emerald-200 bg-emerald-50'
                      : n.tone === 'amber' ? 'border-amber-200 bg-amber-50'
                        : n.tone === 'red' ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'
                  }`}>
                    <Info className={`w-4 h-4 shrink-0 mt-0.5 ${
                      n.tone === 'green' ? 'text-emerald-600' : n.tone === 'amber' ? 'text-amber-600' : n.tone === 'red' ? 'text-red-600' : 'text-gray-500'
                    }`} />
                    <p className="text-[11px] text-gray-800 leading-relaxed">{n.text}</p>
                  </div>
                ));
              })()}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center pt-3 border-t border-gray-100">
            <img src="/Canon_logo_vector.png" alt="Canon" className="h-7 w-auto object-contain" />
            <p className="text-[9px] text-gray-400">{plan.label} · Confidential · Jellybon × Glowytics</p>
            <img src="/Jellybon-digital-agency-istanbul.webp" alt="Jellybon" className="h-6 w-auto object-contain" />
          </div>
        </div>
      </div>

      {byId.size === 0 && (
        <p className="text-center text-sm text-gray-500 mt-6">Bu ay için CCMS kampanyası bulunamadı.</p>
      )}
    </div>
  );
}
